import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';
import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  LEGACY_AGENT_ONLY_PERMISSIONS,
  CONVERSATION_PERMISSIONS,
  INBOX_VIEW_PERMISSIONS,
  SEARCH_PERMISSIONS,
  NOTIFICATIONS_PERMISSIONS,
  ENGAGE_VIEW_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  REPORT_OVERVIEW_PERMISSIONS,
  REPORT_CONVERSATION_PERMISSIONS,
  REPORT_AGENT_PERMISSIONS,
  REPORT_CASE_CLOSING_TYPES_PERMISSIONS,
  REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS,
  REPORT_LABEL_PERMISSIONS,
  REPORT_INBOX_PERMISSIONS,
  REPORT_TEAM_PERMISSIONS,
  REPORT_VIP_PERMISSIONS,
  REPORT_VIP_AGENT_PERMISSIONS,
  REPORT_ISSUE_PERMISSIONS,
  REPORT_TOKEN_USAGE_PERMISSIONS,
  REPORT_CSAT_PERMISSIONS,
  REPORT_SLA_PERMISSIONS,
  REPORT_BOT_PERMISSIONS,
  REPORT_TELEGRAM_BOT_PERMISSIONS,
  LINE_DELIVERY_REPORTS_PERMISSIONS,
  CROSS_BRAND_REPORTS_PERMISSIONS,
  QA_DASHBOARD_VIEW_PERMISSIONS,
  QA_LIVE_MONITOR_VIEW_PERMISSIONS,
  CUSTOM_ROLE_PERMISSIONS,
  TEAM_MANAGEMENT_VIEW_PERMISSIONS,
  AILAB_PERMISSIONS,
  CAMPAIGN_PERMISSIONS,
  TELEGRAM_INTERNAL_VIEW_PERMISSIONS,
  PROFILE_PERMISSIONS,
  PORTAL_PERMISSIONS,
  VIP_PERMISSIONS,
  SLA_PERMISSIONS,
  CUSTOM_ATTRIBUTE_PERMISSIONS,
  AUDIT_LOG_PERMISSIONS,
  CANNED_RESPONSE_PERMISSIONS,
  CANNED_RESPONSE_PUBLIC_PERMISSIONS,
  CANNED_RESPONSE_PRIVATE_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (
  route,
  userPermissions = [],
  options = {}
) => {
  const {
    accountId = null,
    rootGetters = {},
    isEnterprise = typeof window !== 'undefined'
      ? (window?.chatwootConfig?.isEnterprise ?? true)
      : true,
  } = options;
  const {
    meta: {
      permissions: routePermissions = [],
      featureFlag = null,
      showWhenFeatureDisabled = false,
      installationTypes = [],
    } = {},
  } = route;
  const hasRoutePermission = hasPermissions(routePermissions, userPermissions);
  if (!hasRoutePermission) return false;

  if (featureFlag) {
    const keepVisibleWhenDisabled =
      showWhenFeatureDisabled || featureFlag === FEATURE_FLAGS.CAPTAIN;
    const isFeatureEnabledOnAccount =
      rootGetters['accounts/isFeatureEnabledonAccount'];
    if (typeof isFeatureEnabledOnAccount === 'function' && accountId) {
      const enabled = isFeatureEnabledOnAccount(Number(accountId), featureFlag);
      if (!enabled && !keepVisibleWhenDisabled) return false;
    }
  }

  if (Array.isArray(installationTypes) && installationTypes.length > 0) {
    const isOnChatwootCloud = Boolean(
      rootGetters['globalConfig/isOnChatwootCloud']
    );
    const installationCheck = {
      [INSTALLATION_TYPES.ENTERPRISE]: Boolean(isEnterprise),
      [INSTALLATION_TYPES.CLOUD]: isOnChatwootCloud,
      [INSTALLATION_TYPES.COMMUNITY]: true,
    };
    const matchesInstallation = installationTypes.some(
      type => installationCheck[type]
    );
    if (!matchesInstallation) return false;
  }

  return true;
};

const REPORT_LANDING_ROUTES = [
  {
    permissions: ['administrator', REPORTS_PERMISSIONS],
    name: 'account_overview_reports',
    path: 'reports/overview',
  },
  {
    permissions: ['administrator', REPORT_OVERVIEW_PERMISSIONS],
    name: 'account_last_7_days_reports',
    path: 'reports/last_7_days',
  },
  {
    permissions: ['administrator', REPORT_CONVERSATION_PERMISSIONS],
    name: 'conversation_reports',
    path: 'reports/conversation',
  },
  {
    permissions: ['administrator', REPORT_AGENT_PERMISSIONS],
    name: 'agent_reports_index',
    path: 'reports/agents_overview',
  },
  {
    permissions: ['administrator', REPORT_CASE_CLOSING_TYPES_PERMISSIONS],
    name: 'case_closing_types_reports',
    path: 'reports/case_closing_types',
  },
  {
    permissions: ['administrator', REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS],
    name: 'chat_history_export_reports',
    path: 'reports/chat_history_export',
  },
  {
    permissions: ['administrator', REPORT_LABEL_PERMISSIONS],
    name: 'label_reports_index',
    path: 'reports/labels_overview',
  },
  {
    permissions: ['administrator', REPORT_INBOX_PERMISSIONS],
    name: 'inbox_reports_index',
    path: 'reports/inboxes_overview',
  },
  {
    permissions: ['administrator', REPORT_TEAM_PERMISSIONS],
    name: 'team_reports_index',
    path: 'reports/teams_overview',
  },
  {
    permissions: ['administrator', REPORT_VIP_PERMISSIONS],
    name: 'vip_reports_index',
    path: 'reports/vip_overview',
  },
  {
    permissions: ['administrator', REPORT_VIP_AGENT_PERMISSIONS],
    name: 'vip_agent_reports_index',
    path: 'reports/vip_agents_overview',
  },
  {
    permissions: ['administrator', REPORT_ISSUE_PERMISSIONS],
    name: 'issue_reports_index',
    path: 'reports/issues_overview',
  },
  {
    permissions: ['administrator', REPORT_TOKEN_USAGE_PERMISSIONS],
    name: 'token_usage_reports',
    path: 'reports/token_usage',
  },
  {
    permissions: ['administrator', REPORT_CSAT_PERMISSIONS],
    name: 'csat_reports',
    path: 'reports/csat',
  },
  {
    permissions: ['administrator', REPORT_SLA_PERMISSIONS],
    name: 'sla_reports',
    path: 'reports/sla',
  },
  {
    permissions: ['administrator', REPORT_BOT_PERMISSIONS],
    name: 'bot_reports',
    path: 'reports/bot',
  },
  {
    permissions: ['administrator', REPORT_TELEGRAM_BOT_PERMISSIONS],
    name: 'telegram_bot_reports',
    path: 'reports/telegram_bot',
  },
  {
    permissions: ['administrator', LINE_DELIVERY_REPORTS_PERMISSIONS],
    name: 'line_delivery_reports',
    path: 'reports/line_delivery',
  },
  {
    permissions: ['administrator', CROSS_BRAND_REPORTS_PERMISSIONS],
    name: 'cross_brand_reports_index',
    path: 'reports/all_brands',
  },
];

const findFirstAccessibleLandingRoute = (routeCandidates, permissions) => {
  return (
    routeCandidates.find(({ permissions: routePermissions }) =>
      hasPermissions(routePermissions, permissions)
    ) || null
  );
};

export const getDefaultReportsRouteName = permissions => {
  return findFirstAccessibleLandingRoute(REPORT_LANDING_ROUTES, permissions)
    ?.name;
};

const getDefaultReportsRoutePath = permissions => {
  return findFirstAccessibleLandingRoute(REPORT_LANDING_ROUTES, permissions)
    ?.path;
};

export const defaultRedirectPageForAccount = (accountId, permissions) => {
  const permissionRoutes = [
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          CONVERSATION_PERMISSIONS,
        ],
      },
      path: 'dashboard',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          INBOX_VIEW_PERMISSIONS,
        ],
      },
      path: 'inbox-view',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          SEARCH_PERMISSIONS,
        ],
      },
      path: 'search',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          NOTIFICATIONS_PERMISSIONS,
        ],
      },
      path: 'notifications',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          ENGAGE_VIEW_PERMISSIONS,
        ],
      },
      path: 'engage/traffic',
    },
    {
      permissions: ['administrator', CAMPAIGN_PERMISSIONS],
      path: 'campaigns/telegram',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          AILAB_PERMISSIONS,
        ],
      },
      path: 'ailab/assistants',
    },
    {
      permissions: ['administrator', TELEGRAM_INTERNAL_VIEW_PERMISSIONS],
      path: 'telegram-internal/queue',
    },
    {
      permissions: {
        anyOf: ['administrator', TEAM_MANAGEMENT_VIEW_PERMISSIONS],
      },
      path: 'team-management/agents',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          PROFILE_PERMISSIONS,
        ],
      },
      path: 'profile/settings',
    },
    {
      permissions: [
        'administrator',
        CANNED_RESPONSE_PERMISSIONS,
        CANNED_RESPONSE_PUBLIC_PERMISSIONS,
        CANNED_RESPONSE_PRIVATE_PERMISSIONS,
      ],
      path: 'settings/canned-response/list',
    },
    {
      permissions: ['administrator', CUSTOM_ROLE_PERMISSIONS],
      path: 'settings/custom-roles/list',
    },
    {
      permissions: ['administrator', VIP_PERMISSIONS],
      path: 'settings/vip',
    },
    {
      permissions: ['administrator', SLA_PERMISSIONS],
      path: 'settings/sla/list',
    },
    {
      permissions: ['administrator', CUSTOM_ATTRIBUTE_PERMISSIONS],
      path: 'settings/custom-attributes/list',
    },
    {
      permissions: ['administrator', AUDIT_LOG_PERMISSIONS],
      path: 'settings/audit-logs/list',
    },
    {
      permissions: {
        anyOf: [
          'administrator',
          LEGACY_AGENT_ONLY_PERMISSIONS,
          CONTACT_PERMISSIONS,
        ],
      },
      path: 'contacts',
    },
    ...REPORT_LANDING_ROUTES.map(({ permissions: routePermissions, path }) => ({
      permissions: routePermissions,
      path,
    })),
    {
      permissions: ['administrator', QA_DASHBOARD_VIEW_PERMISSIONS],
      path: 'qa-dashboard/template-audit',
    },
    {
      permissions: ['administrator', QA_LIVE_MONITOR_VIEW_PERMISSIONS],
      path: 'qa-monitor',
    },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route =
    permissionRoutes.find(({ permissions: routePermissions }) =>
      hasPermissions(routePermissions, permissions)
    ) ||
    (getDefaultReportsRoutePath(permissions)
      ? { path: getDefaultReportsRoutePath(permissions) }
      : null);

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;
  return defaultRedirectPageForAccount(accountId, permissions);
};

const validateActiveAccountRoutes = (to, user, rootGetters = {}) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions, {
    accountId: to.params.accountId,
    rootGetters,
  });
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user, rootGetters = {}) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, rootGetters);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'conversation_vip',
    'conversation_wlab_ai_agent_all',
    'conversation_wlab_ai_agent_resolved',
    'conversation_wlab_ai_agent_handoff',
    'conversation_wlab_ai_agent_pending',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_vip',
    'conversation_through_wlab_ai_agent_all',
    'conversation_through_wlab_ai_agent_resolved',
    'conversation_through_wlab_ai_agent_handoff',
    'conversation_through_wlab_ai_agent_pending',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversation_through_vip':
      return 'conversation_vip';
    case 'conversation_through_wlab_ai_agent_all':
      return 'conversation_wlab_ai_agent_all';
    case 'conversation_through_wlab_ai_agent_resolved':
      return 'conversation_wlab_ai_agent_resolved';
    case 'conversation_through_wlab_ai_agent_handoff':
      return 'conversation_wlab_ai_agent_handoff';
    case 'conversation_through_wlab_ai_agent_pending':
      return 'conversation_wlab_ai_agent_pending';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
